import React, { useState, useEffect, useRef, memo } from "react";
import { toast } from "react-hot-toast";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { IoSearch } from "react-icons/io5";
import Modal from "react-modal";
import { MdFileDownload } from "react-icons/md";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { motion } from "framer-motion";
import backarrow from "../assets/icon/arrow-right.svg";
import trashIcon from "../assets/icon/trash.svg";
import {
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
} from "react-icons/fa";

const AnimatedBackground = memo(() => {
  const icons = [
    FaBook,
    FaBookOpen,
    FaRegListAlt,
    FaDatabase,
    FaUserGraduate,
    FaChalkboardTeacher,
    FaCalculator,
    FaChartLine,
    FaCoins,
    FaPenFancy,
    FaRegFileAlt,
    FaPencilAlt,
  ];

  const rows = 5;
  const cols = 11;
  const iconArray = [];

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const Icon = icons[Math.floor(Math.random() * icons.length)];
      iconArray.push({
        Icon,
        size: 18 + Math.random() * 20,
        top: (i / rows) * 100 + Math.random() * 5,
        left: (j / cols) * 100 + Math.random() * 5,
        duration: 15 + Math.random() * 10,
        opacity: 0.15 + Math.random() * 0.25,
        xOffset: Math.random() * 10 - 5,
        yOffset: Math.random() * 10 - 5,
      });
    }
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {iconArray.map((item, i) => (
        <motion.div
          key={i}
          className="absolute text-white/50"
          style={{
            top: `${item.top}%`,
            left: `${item.left}%`,
            opacity: item.opacity,
          }}
          initial={{ scale: 0.5 }}
          animate={{
            x: [0, item.xOffset, 0],
            y: [0, item.yOffset, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: item.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <item.Icon size={item.size} />
        </motion.div>
      ))}
    </div>
  );
});

const AttendanceList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const courseid = location.state?.courseid;
  const API_BASE = process.env.REACT_APP_API_URL;
  const authHeader = { Authorization: localStorage.getItem("token") };

  const [allstudent, setAllstudent] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [currentStudents, setCurrentStudents] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  const [datevalue, setDateValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalStudentPages, setTotalStudentPages] = useState(1);
  const [toggling, setToggling] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteid, setDeleteid] = useState("");

  const studentFilterRef = useRef("");
  const calendarRef = useRef(null);
  const debounceTimeout = useRef(null);

  const customStyles = {
    content: {
      borderRadius: "16px",
      margin: "auto",
      padding: "0",
      border: "none",
      width: "320px",
      maxWidth: "320px",
      height: "180px",
      background: "white",
    },
    overlay: {
      backgroundColor: "rgba(0,0,0,0.55)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  const getStudents = async () => {
    if (!courseid) return;
    setSearchLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/course/Get-Single-Course-By-Id/${courseid}`,
        {
          params: { search: studentFilterRef.current.trim() || "" },
          headers: authHeader,
        }
      );
      setAllstudent(res.data.data.Students || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطای دریافت دانشجویان");
    } finally {
      setSearchLoading(false);
      setLoading(false);
    }
  };

  const getSessions = async () => {
    if (!courseid) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/attendance/GetSessions/${courseid}`,
        { headers: authHeader }
      );
      const fetchedSessions = Array.isArray(res.data) ? res.data : [];
      setSessions(fetchedSessions);
      if (fetchedSessions.length > 0) {
        const lastSession = fetchedSessions[fetchedSessions.length - 1].Session;
        setCurrentSession(lastSession);
      }
    } catch (error) {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudents();
    getSessions();
  }, [courseid]);

  useEffect(() => {
    if (!sessions.length || !currentSession) {
      setCurrentStudents([]);
      setDateValue(null);
      return;
    }

    const sess = sessions.find((s) => s.Session === currentSession);
    if (!sess) return;

    const dateObj = new DateObject(sess.Date_Classes);
    setDateValue(dateObj);

    const statusMap = new Map();
    (sess.StudentsStatus || []).forEach((st) => {
      const id = st?.studentId?._id || st?.studentId;
      statusMap.set(String(id), st);
    });

    const rows = allstudent.map((student) => {
      const id = String(student._id || student.id);
      const st = statusMap.get(id);
      return {
        _id: id,
        firstName: student.firstName || "",
        lastName: student.lastName || "",
        isPresent: st ? !!st.isPresent : false,
      };
    });

    setCurrentStudents(rows);
    setTotalStudentPages(Math.max(1, Math.ceil(rows.length / studentsPerPage)));
  }, [currentSession, sessions, allstudent, studentsPerPage]);

  const handleDateChange = async (date) => {
    if (!date || !currentSession) return;
    setDateValue(date);

    const gregorianDate = date.convert(gregorian, gregorian_en);
    const formattedDate = gregorianDate.format("YYYY-MM-DD");

    const sess = sessions.find((s) => s.Session === currentSession);
    try {
      const res = await axios.put(
        `${API_BASE}/api/attendance/UpdateSessionDate/${sess._id}`,
        { Date_Classes: formattedDate },
        { headers: authHeader }
      );
      setSessions((prev) =>
        prev.map((s) => (s._id === res.data.session._id ? res.data.session : s))
      );
      toast.success("تاریخ جلسه بروزرسانی شد ✔");
    } catch (error) {
      toast.error("خطا در بروزرسانی تاریخ");
    }
  };

  const addSession = async () => {
    try {
      const nextNumber =
        sessions.length > 0
          ? (parseInt(sessions[sessions.length - 1].Session) + 1).toString()
          : "1";
      const today = new DateObject()
        .convert(gregorian, gregorian_en)
        .format("YYYY-MM-DD");

      const res = await axios.post(
        `${API_BASE}/api/attendance/AddSession`,
        { CourseId: courseid, Session: nextNumber, Date_Classes: today },
        { headers: authHeader }
      );

      const newSession = res.data.attendance;
      setSessions((prev) => [...prev, newSession]);
      setCurrentSession(newSession.Session);
      toast.success("جلسه جدید ساخته شد ✔");
    } catch (error) {
      toast.error("خطا در ساخت جلسه");
    }
  };

  const toggleAttendance = async (studentId) => {
    const sess = sessions.find((s) => s.Session === currentSession);
    if (!sess) return;

    const studentStatus = (sess.StudentsStatus || []).find(
      (st) => String(st?.studentId?._id || st?.studentId) === String(studentId)
    );
    const updatedPresence = studentStatus ? !studentStatus.isPresent : true;

    try {
      setToggling(studentId);
      const res = await axios.put(
        `${API_BASE}/api/attendance/UpdateStudent/${sess._id}/${studentId}`,
        { isPresent: updatedPresence },
        { headers: authHeader }
      );
      setSessions((prev) =>
        prev.map((s) => (s._id === res.data.session._id ? res.data.session : s))
      );
      setCurrentStudents((prev) =>
        prev.map((st) =>
          String(st._id) === String(studentId)
            ? { ...st, isPresent: updatedPresence }
            : st
        )
      );
    } catch (error) {
      toast.error("خطا در تغییر وضعیت حضور");
    } finally {
      setToggling(null);
    }
  };

  const openDeleteModal = () => {
    const curr = sessions.find((s) => s.Session === currentSession);
    if (curr) {
      setDeleteid(curr._id);
      setModalIsOpen(true);
    } else {
      toast.error("جلسه یافت نشد!");
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteid) return;
    try {
      await axios.delete(
        `${API_BASE}/api/attendance/DeleteSession/${deleteid}`,
        { headers: authHeader }
      );
      toast.success("جلسه با موفقیت حذف شد");
      await getSessions();
      if (sessions.length <= 1) setCurrentSession(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطا در حذف جلسه");
    } finally {
      setModalIsOpen(false);
      setDeleteid("");
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/attendance/create_excel_file_all_session/${courseid}`,
        { responseType: "blob", headers: authHeader }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "Attendance.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("فایل اکسل دانلود شد");
    } catch (error) {
      toast.error("خطا در دانلود فایل");
    }
  };

  const handleStudentSearch = (value) => {
    studentFilterRef.current = value;
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    setSearchLoading(true);
    debounceTimeout.current = setTimeout(() => getStudents(), 800);
  };

  useEffect(() => {
    if (!courseid) navigate("/Mainpage/Attendance");
  }, [courseid, navigate]);

  const studentStartIndex = (studentPage - 1) * studentsPerPage;
  const paginatedStudents = currentStudents.slice(
    studentStartIndex,
    studentStartIndex + studentsPerPage
  );

  const visiblePages = window.innerWidth < 768 ? 2 : 4;
  const half = Math.floor(visiblePages / 2);
  let startStudentPage = Math.max(1, studentPage - half);
  let endStudentPage = Math.min(
    totalStudentPages,
    startStudentPage + visiblePages - 1
  );
  if (endStudentPage - startStudentPage + 1 < visiblePages) {
    startStudentPage = Math.max(1, endStudentPage - visiblePages + 1);
  }

  return (
    <div className="relative min-h-screen w-full bg-gray-800 flex items-center justify-center p-4">
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-[50rem] mx-auto border border-white/30 overflow-hidden mt-11"
      >
        {/* هدر */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <img
            src={backarrow}
            alt="back"
            className="w-8 h-8 cursor-pointer hover:scale-110 transition"
            onClick={() => navigate("/Mainpage/Attendance")}
          />
          <h2 className="text-2xl font-bold text-gray-800">حضور و غیاب</h2>
          <div className="w-8" />
        </div>

        <div className="p-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div ref={calendarRef}>
              <DatePicker
                value={datevalue}
                onChange={handleDateChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                placeholder="انتخاب تاریخ جلسه"
                inputClass="w-full px-5 py-3.5 rounded-xl border-2 border-gray-300 focus:border-gray-700 outline-none text-gray-800 font-medium transition bg-white/70 text-right"
                containerClassName="w-full"
                calendarPosition="bottom-center"
              />
            </div>

            {/* جستجو دانشجو */}
            <div className="relative">
              <input
                type="text"
                placeholder="جستجو ..."
                onChange={(e) => handleStudentSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 focus:border-gray-700 outline-none text-gray-800 font-medium"
              />
              <IoSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
            </div>

            {/* دانلود اکسل */}
            <button
              onClick={handleDownload}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-xl transition font-medium"
            >
              <MdFileDownload size={22} />
              دانلود اکسل
            </button>
          </div>

          {/* لیست جلسات */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                جلسات برگزار شده
              </h3>
              <button
                onClick={addSession}
                className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white rounded-xl font-medium transition"
              >
                + جلسه جدید
              </button>
            </div>

            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-xl bg-white shadow-inner">
              {sessions.length === 0 ? (
                <p className="text-center py-10 text-gray-500 font-medium">
                  هنوز جلسه‌ای برگزار نشده
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
                  {sessions.map((s) => (
                    <motion.button
                      key={s._id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setCurrentSession(s.Session)}
                      className={`py-3 rounded-xl font-bold transition ${
                        currentSession === s.Session
                          ? "bg-gray-800 text-white shadow-lg"
                          : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                      }`}
                    >
                      جلسه {s.Session}
                    </motion.button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* جدول حضور و غیاب */}
          {currentSession && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  وضعیت حضور - جلسه {currentSession}
                </h3>
                <motion.div
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={openDeleteModal}
                  className="cursor-pointer"
                  title="حذف جلسه"
                >
                  <img
                    src={trashIcon}
                    alt="حذف"
                    className="w-7 h-7 opacity-80 hover:opacity-100"
                  />
                </motion.div>
              </div>

              <div className="overflow-x-auto rounded-xl shadow-lg border border-gray-200">
                {loading || searchLoading ? (
                  <div className="text-center py-16">
                    <p className="text-xl font-bold text-gray-600">
                      در حال بارگذاری...
                    </p>
                  </div>
                ) : paginatedStudents.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-xl font-bold text-gray-600">
                      دانشجویی یافت نشد
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800 text-white font-iranSansNormal">
                      <tr>
                        <th className="py-4 px-6 text-center">#</th>
                        <th className="py-4 px-6 text-right">نام</th>
                        <th className="py-4 px-6 text-right">نام خانوادگی</th>
                        <th className="py-4 px-6 text-center">وضعیت حضور</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStudents.map((student, idx) => (
                        <tr
                          key={student._id}
                          className="border-b border-gray-200 hover:bg-gray-50 transition"
                        >
                          <motion.td
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="py-4 px-6 text-center font-medium"
                          >
                            {studentStartIndex + idx + 1}
                          </motion.td>
                          <motion.td
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 + 0.05 }}
                            className="py-4 px-6 text-right font-medium text-gray-800"
                          >
                            {student.firstName}
                          </motion.td>
                          <motion.td
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 + 0.1 }}
                            className="py-4 px-6 text-right font-medium text-gray-800"
                          >
                            {student.lastName}
                          </motion.td>
                          <motion.td
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 + 0.15 }}
                            className="py-4 px-6 text-center"
                          >
                            <button
                              onClick={() => toggleAttendance(student._id)}
                              disabled={toggling === student._id}
                              className={`px-8 py-2.5 rounded-xl font-iranSansNormal transition shadow ${
                                toggling === student._id
                                  ? "bg-gray-400 text-white"
                                  : student.isPresent
                                  ? "bg-green-600 text-white hover:bg-green-700"
                                  : "bg-red-600 text-white hover:bg-red-700"
                              }`}
                            >
                              {toggling === student._id
                                ? "در حال تغییر..."
                                : student.isPresent
                                ? "حاضر"
                                : "غایب"}
                            </button>
                          </motion.td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* صفحه‌بندی دانشجویان */}
              {totalStudentPages > 1 && (
                <div className="flex justify-center items-center gap-3 mt-8">
                  <button
                    onClick={() => setStudentPage((p) => Math.max(1, p - 1))}
                    disabled={studentPage === 1}
                    className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition"
                  >
                    <FaArrowRight size={20} />
                  </button>

                  {Array.from(
                    { length: endStudentPage - startStudentPage + 1 },
                    (_, i) => startStudentPage + i
                  ).map((num) => (
                    <motion.button
                      key={num}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setStudentPage(num)}
                      className={`w-10 h-10 rounded-full font-bold transition ${
                        num === studentPage
                          ? "bg-gray-800 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {num}
                    </motion.button>
                  ))}

                  <button
                    onClick={() =>
                      setStudentPage((p) => Math.min(totalStudentPages, p + 1))
                    }
                    disabled={studentPage === totalStudentPages}
                    className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition"
                  >
                    <FaArrowLeft size={20} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* مودال حذف جلسه */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        style={customStyles}
      >
        <div className="flex flex-col items-center justify-center h-full px-6 text-center bg-gray-200 font-iranSansNormal">
          <p className="text-gray-800 text-lg font-semibold">
            آیا از حذف این جلسه اطمینان دارید؟
          </p>
          <div className="flex gap-4 mt-6">
            <button
              onClick={handleDeleteSession}
              className="px-8 py-2.5 rounded-lg bg-red-600/90 text-white text-sm font-medium"
            >
              بله
            </button>
            <button
              onClick={() => setModalIsOpen(false)}
              className="px-8 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium"
            >
              خیر
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AttendanceList;
